/**
 * Conversation Store (Zustand)
 * Manages conversation state globally for chat interface.
 * Uses server-side persistence via the conversation sessions API.
 *
 * Composed from focused slices — each slice owns a domain of state + actions.
 * The full ConversationState type and shared helpers live in separate files.
 */

import { AccessibilityInfo } from "react-native";
import { create } from "zustand";
import type { Socket } from "socket.io-client";
import { onSocketReady, useSocketStatus } from "../services/socket";
import { onServerEvent } from "../services/socketValidation";
import { useAuthStore } from "./authStore";
import { useStreamingTTSStore } from "./streamingTTSStore";
import { createAnalysisError } from "../utils/conversationErrors";
import { createLogger } from "../utils/logger";
import type {
  PeerMessageSavedPayload,
  PeerDonePayload,
  PeerAnalysisCompletePayload,
  PeerAnalysisFailedPayload,
  ParticipantActivityPayload,
} from "../generated/api/socket-events";
import type { AnalyzedMessage, ConversationAnalysis } from "../types/conversation";

import type { ConversationState } from "./conversationTypes";
import { createSessionSlice } from "./slices/sessionSlice";
import { createMessagesSlice, setMessagesSliceStoreRef } from "./slices/messagesSlice";
import { createRecordingSlice } from "./slices/recordingSlice";
import { createAnalysisSlice } from "./slices/analysisSlice";

// Re-export the state type for consumers
export type { ConversationState } from "./conversationTypes";

const log = createLogger("ConversationStore");

export const useConversationStore = create<ConversationState>()((...a) => ({
  ...createSessionSlice(...a),
  ...createMessagesSlice(...a),
  ...createRecordingSlice(...a),
  ...createAnalysisSlice(...a),
}));

// Wire up the lazy store reference for streamMessage's session transition callback
setMessagesSliceStoreRef(useConversationStore);


// ── Stream recovery: disconnect/reconnect handling ──────────────────
// Extracted to streamRecovery.ts for testability.
import { createStreamRecovery } from './streamRecovery';

const streamRecovery = createStreamRecovery({
  conversation: {
    setState: (updater) => useConversationStore.setState(updater as (s: ConversationState) => Partial<ConversationState>),
    getState: () => useConversationStore.getState(),
    retryConversation: (id) => useConversationStore.getState().retryConversation(id),
  },
  tts: {
    cancel: () => useStreamingTTSStore.getState().cancel(),
  },
});

useSocketStatus.subscribe((state, prev) => {
  if (state.status !== 'connected' && prev.status === 'connected') {
    streamRecovery.onDisconnect();
  }
  if (state.status === 'connected' && prev.status !== 'connected') {
    streamRecovery.onReconnect();
  }
});

// ── Multi-device peer listeners ─────────────────────────────────────
// Registered once per socket connection via onSocketReady callback.
// Handles messages sent from other devices on the same account.

function registerPeerListeners(socket: Socket): void {
  // Guard against duplicate registration (reconnect creates a new socket)
  // Note: these remove ALL listeners for each event (no handler arg) — intentional guard
  socket.off('peer_message_saved');
  socket.off('peer_done');
  socket.off('peer_analysis_complete');
  socket.off('peer_analysis_failed');
  socket.off('participant_activity');

  // Another device sent a message — show typing indicator
  onServerEvent(socket, 'peer_message_saved', (data: PeerMessageSavedPayload) => {
    const state = useConversationStore.getState();
    if (state.currentSessionId !== data.sessionId) return;

    const { user } = useAuthStore.getState();

    // Add the user message from the peer device
    const userMsg: AnalyzedMessage = {
      id: data.userMessageId,
      type: 'text',
      text: data.userText,
      userId: user?.id || '',
      sessionId: data.sessionId,
      createdAt: new Date(),
      status: 'complete',
      role: 'user',
      analysisPending: useConversationStore.getState().hasAnalysis,
    };

    useConversationStore.setState((s) => ({
      messages: [...s.messages, userMsg],
      peerTyping: true,
    }));
  });

  // Another device got a complete AI response
  onServerEvent(socket, 'peer_done', (data: PeerDonePayload) => {
    const state = useConversationStore.getState();
    if (state.currentSessionId !== data.sessionId) return;

    const { user } = useAuthStore.getState();

    // Check if user message was already added by peer_message_saved
    const hasUserMsg = state.messages.some((m) => m.id === data.userMessageId);

    const aiMsg: AnalyzedMessage = {
      id: data.aiMessageId,
      type: 'text',
      text: data.aiText,
      userId: user?.id || '',
      sessionId: data.sessionId,
      createdAt: new Date(),
      status: 'complete',
      role: 'assistant',
    };

    useConversationStore.setState((s) => ({
      messages: hasUserMsg
        ? [...s.messages, aiMsg]
        : [
            ...s.messages,
            {
              id: data.userMessageId,
              type: 'text',
              text: data.userText,
              userId: user?.id || '',
              sessionId: data.sessionId,
              createdAt: new Date(),
              status: 'complete',
              role: 'user',
              analysisPending: useConversationStore.getState().hasAnalysis,
            } as AnalyzedMessage,
            aiMsg,
          ],
      peerTyping: false,
    }));

    AccessibilityInfo.announceForAccessibility?.('New message from another device');
  });

  // Peer's analysis completed
  onServerEvent(socket, 'peer_analysis_complete', (data: PeerAnalysisCompletePayload) => {
    const state = useConversationStore.getState();
    if (state.currentSessionId !== data.sessionId) return;

    useConversationStore.setState((s) => ({
      messages: s.messages.map((m) =>
        m.id === data.userMessageId
          ? { ...m, analysis: data.analysis as unknown as ConversationAnalysis | undefined, isAnalysisExpanded: false, analysisPending: false }
          : m
      ),
    }));
  });

  // Peer's analysis failed — clear pending state
  onServerEvent(socket, 'peer_analysis_failed', (data: PeerAnalysisFailedPayload) => {
    const state = useConversationStore.getState();
    if (state.currentSessionId !== data.sessionId) return;

    useConversationStore.setState((s) => ({
      messages: s.messages.map((m) =>
        m.id === data.userMessageId
          ? { ...m, analysisPending: false, analysisError: createAnalysisError() }
          : m
      ),
    }));
  });

  // Activity/presence events (typing indicators for bots and other users)
  onServerEvent(socket, 'participant_activity', (data: ParticipantActivityPayload) => {
    const state = useConversationStore.getState();
    if (state.currentSessionId !== data.sessionId) return;

    if (data.actor.kind === 'bot') {
      useConversationStore.setState({
        peerTyping: data.type === 'typing',
      });
    }
    // Future: handle user typing indicators for multi-user sessions
  });

  log.info('Peer listeners registered');
}

// Wire up peer listeners — called when socket connects (or immediately if already connected)
onSocketReady(registerPeerListeners);
